<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import { PaymentService } from '@/services/payment.services';
import RefundDialog from './RefundDialog.vue';

const route = useRoute();
const router = useRouter();
const { t } = useI18n({ useScope: 'global' });
const queryClient = useQueryClient();

const paymentId = computed(() => route.params.id as string);

const { data, isPending, isError } = useQuery({
  queryKey: computed(() => ['pm-payment-detail', paymentId.value]),
  queryFn: async () => {
    const res = await PaymentService.getPaymentById(paymentId.value);
    return res.data?.data ?? res.data;
  },
  enabled: computed(() => !!paymentId.value),
});

// ── Actions rapides (rembourser / resynchroniser) ─────────────────────────────
const refundDialogOpen = ref(false);
const isReconciling = ref(false);

function openRefundDialog() {
  refundDialogOpen.value = true;
}
function closeRefundDialog() {
  refundDialogOpen.value = false;
}
async function onRefunded() {
  await queryClient.invalidateQueries({
    queryKey: ['pm-payment-detail', paymentId.value],
  });
}
const reconcileError = ref('');
async function reconcile() {
  isReconciling.value = true;
  reconcileError.value = '';
  try {
    await PaymentService.reconcilePayment(paymentId.value);
    await queryClient.invalidateQueries({
      queryKey: ['pm-payment-detail', paymentId.value],
    });
  } catch (err: any) {
    const msgs = err?.response?.data?.message;
    reconcileError.value = Array.isArray(msgs)
      ? msgs.join(' · ')
      : msgs || t('paymentsMgmt.reconcileError');
  } finally {
    isReconciling.value = false;
  }
}

const payment = computed(() => data.value?.payment ?? null);
const followups = computed<any[]>(() => data.value?.followups ?? []);
const order = computed(() => data.value?.order ?? null);
const user = computed(() => data.value?.user ?? null);
const store = computed(() => data.value?.store ?? null);
const taxUsages = computed<any[]>(() => data.value?.taxUsages ?? []);

function formatAmount(amount: number | null | undefined, cur?: string | null) {
  if (amount === null || amount === undefined) return '—';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: cur ?? 'EUR',
    }).format(amount);
  } catch {
    return `${amount} ${cur ?? ''}`;
  }
}
function formatDate(d: string | null | undefined) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
function statusColor(status: string) {
  switch (status) {
    case 'totaly_paid':
      return '#10b981';
    case 'partialy_paid':
      return '#3b82f6';
    case 'pending':
      return '#f59e0b';
    case 'cancel':
      return '#94a3b8';
    case 'failed':
      return '#ef4444';
    default:
      return '#64748b';
  }
}
function statusLabel(status: string | undefined) {
  if (!status) return '—';
  return t(`paymentsMgmt.status.${status}`);
}
function refundStatusColor(status: string) {
  switch (status) {
    case 'totaly_refunded':
      return '#8b5cf6';
    case 'partialy_refunded':
      return '#3b82f6';
    case 'pending':
      return '#f59e0b';
    case 'failed':
      return '#ef4444';
    default:
      return '#64748b';
  }
}
function refundStatusLabel(status: string) {
  return t(`paymentsMgmt.refundStatus.${status}`);
}
function hasActiveRefund(p: any) {
  return p?.refundStatus && p.refundStatus !== 'none';
}
function isFullyRefunded(p: any) {
  return p?.refundStatus === 'totaly_refunded';
}
function effectiveStatusColor(p: any) {
  return hasActiveRefund(p)
    ? refundStatusColor(p.refundStatus)
    : statusColor(p.status);
}
function effectiveStatusLabel(p: any) {
  return hasActiveRefund(p)
    ? refundStatusLabel(p.refundStatus)
    : statusLabel(p.status);
}
function orderPaymentStatusColor(status: string) {
  switch (status) {
    case 'paid':
      return '#10b981';
    case 'refunded':
      return '#8b5cf6';
    case 'partially_refunded':
      return '#3b82f6';
    case 'pending':
      return '#f59e0b';
    case 'failed':
      return '#ef4444';
    default:
      return '#64748b';
  }
}
function orderPaymentStatusLabel(status: string | undefined) {
  if (!status) return '—';
  return t(`paymentsMgmt.orderPaymentStatusValues.${status}`, status);
}
function processorLabel(p: string | undefined) {
  if (!p) return '—';
  return t(`paymentsMgmt.processor.${p}`);
}
function userName(u: any) {
  if (!u) return '—';
  const full = [u.firstname, u.lastname].filter(Boolean).join(' ').trim();
  return full || u.email || 'CNC';
}
function userInitials(u: any) {
  const name = userName(u);
  return name.slice(0, 2).toUpperCase();
}

function goBack() {
  router.push('/gestion-paiement');
}
function goToStore() {
  if (!store.value?.id) return;
  router.push(`/commerces/${store.value.id}`);
}
function goToOrder() {
  if (!order.value?.id) return;
  router.push(`/commandes/${order.value.id}`);
}
</script>

<template>
  <div class="pd">
    <!-- Breadcrumb -->
    <nav class="pd__bc">
      <button class="pd__bc-link" @click="goBack">
        {{ t('paymentsMgmt.title') }}
      </button>
      <i class="pi pi-angle-right pd__bc-sep" />
      <span class="pd__bc-cur">{{ paymentId.slice(0, 8) }}</span>
    </nav>

    <div v-if="isPending" class="pd__loading">
      <i class="pi pi-spin pi-spinner" />
    </div>
    <div v-else-if="isError || !payment" class="pd__error">
      <i class="pi pi-exclamation-circle" />
      <p>{{ t('paymentsMgmt.detailError') }}</p>
      <button class="pd__btn pd__btn--ghost" @click="goBack">
        <i class="pi pi-arrow-left" /> {{ t('paymentsMgmt.title') }}
      </button>
    </div>

    <template v-else>
      <!-- Header -->
      <div class="pd__header">
        <button class="pd__back" @click="goBack">
          <i class="pi pi-arrow-left" />
        </button>
        <div class="pd__header-info">
          <h1 class="pd__amount">
            {{ formatAmount(payment.amount, payment.cur) }}
          </h1>
          <p class="pd__sub">
            {{ processorLabel(payment.processor) }} ·
            {{ formatDate(payment.createdAt) }}
          </p>
        </div>
        <span
          class="pd__pill"
          :style="{
            background: effectiveStatusColor(payment) + '1a',
            color: effectiveStatusColor(payment),
            borderColor: effectiveStatusColor(payment) + '40',
          }"
        >
          {{ effectiveStatusLabel(payment) }}
        </span>
        <div class="pd__header-actions">
          <button
            class="pd__btn pd__btn--ghost"
            :disabled="isReconciling"
            @click="reconcile"
          >
            <span v-if="isReconciling" class="pd__spinner" />
            <i v-else class="pi pi-sync" />
            {{ t('paymentsMgmt.reconcile') }}
          </button>
          <button
            class="pd__btn pd__btn--danger"
            :disabled="isFullyRefunded(payment)"
            :title="
              isFullyRefunded(payment) ? t('paymentsMgmt.alreadyRefunded') : ''
            "
            @click="openRefundDialog"
          >
            <i class="pi pi-replay" /> {{ t('paymentsMgmt.refund') }}
          </button>
        </div>
      </div>

      <p v-if="reconcileError" class="pd__reconcile-error">
        <i class="pi pi-exclamation-circle" /> {{ reconcileError }}
      </p>

      <!-- Grille principale -->
      <div class="pd__grid">
        <!-- Colonne gauche -->
        <div class="pd__col">
          <!-- Infos paiement -->
          <div class="pd__card">
            <div class="pd__card-hd">
              <h3 class="pd__card-title">
                <i class="pi pi-wallet" /> {{ t('paymentsMgmt.detailInfo') }}
              </h3>
            </div>
            <div class="pd__info-view">
              <div class="pd__info-row">
                <span class="pd__info-label">{{
                  t('paymentsMgmt.processor.label')
                }}</span>
                <span class="pd__info-value">{{
                  processorLabel(payment.processor)
                }}</span>
              </div>
              <div class="pd__info-row">
                <span class="pd__info-label">{{
                  t('paymentsMgmt.refundStatus.label')
                }}</span>
                <span class="pd__info-value">{{
                  t(`paymentsMgmt.refundStatus.${payment.refundStatus}`)
                }}</span>
              </div>
              <div class="pd__info-row">
                <span class="pd__info-label">{{ t('paymentsMgmt.net') }}</span>
                <span class="pd__info-value">{{
                  formatAmount(payment.net, payment.cur)
                }}</span>
              </div>
              <div class="pd__info-row">
                <span class="pd__info-label">{{ t('paymentsMgmt.tax') }}</span>
                <span class="pd__info-value">{{
                  formatAmount(payment.tax, payment.cur)
                }}</span>
              </div>
              <div class="pd__info-row">
                <span class="pd__info-label">{{
                  t('paymentsMgmt.colDate')
                }}</span>
                <span class="pd__info-value">{{
                  formatDate(payment.createdAt)
                }}</span>
              </div>
              <div v-if="payment.cardLastDigit" class="pd__info-row">
                <span class="pd__info-label">{{ t('paymentsMgmt.card') }}</span>
                <span class="pd__info-value"
                  >•••• {{ payment.cardLastDigit }}</span
                >
              </div>
            </div>
          </div>

          <!-- Transactions provider -->
          <div v-if="followups.length" class="pd__card">
            <div class="pd__card-hd">
              <h3 class="pd__card-title">
                <i class="pi pi-history" />
                {{ t('paymentsMgmt.detailTransactions') }}
              </h3>
            </div>
            <div class="pd__tx-list">
              <div v-for="f in followups" :key="f.id" class="pd__tx-row">
                <span class="pd__tx-type">{{ f.type }}</span>
                <span class="pd__tx-status">{{ f.status }}</span>
                <span class="pd__tx-amount">{{
                  formatAmount(f.amount, f.cur)
                }}</span>
                <span class="pd__tx-date">{{ formatDate(f.createdAt) }}</span>
              </div>
            </div>
          </div>

          <!-- Taxes -->
          <div v-if="taxUsages.length" class="pd__card">
            <div class="pd__card-hd">
              <h3 class="pd__card-title">
                <i class="pi pi-percentage" />
                {{ t('paymentsMgmt.detailTaxes') }}
              </h3>
            </div>
            <div class="pd__tx-list">
              <div
                v-for="tx in taxUsages"
                :key="tx.taxRateId"
                class="pd__tx-row"
              >
                <span class="pd__tx-type">{{
                  tx.taxName ?? tx.taxRateId
                }}</span>
                <span class="pd__tx-status">{{ tx.rateApplied }}%</span>
                <span class="pd__tx-amount">{{
                  formatAmount(tx.taxAmount, payment.cur)
                }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Colonne droite -->
        <div class="pd__col">
          <!-- Client -->
          <div v-if="user" class="pd__card">
            <div class="pd__card-hd">
              <h3 class="pd__card-title">
                <i class="pi pi-user" /> {{ t('paymentsMgmt.detailUser') }}
              </h3>
            </div>
            <div class="pd__entity-row">
              <div class="pd__entity-avatar">{{ userInitials(user) }}</div>
              <div class="pd__entity-info">
                <span class="pd__entity-name">{{ userName(user) }}</span>
                <span class="pd__entity-id">ID · {{ user.id }}</span>
              </div>
            </div>
          </div>

          <!-- Commerce -->
          <div v-if="store" class="pd__card">
            <div class="pd__card-hd">
              <h3 class="pd__card-title">
                <i class="pi pi-shop" /> {{ t('paymentsMgmt.detailStore') }}
              </h3>
            </div>
            <div class="pd__entity-row">
              <div class="pd__entity-avatar"><i class="pi pi-shop" /></div>
              <span class="pd__entity-name">{{
                store.designation ?? '—'
              }}</span>
            </div>
            <div class="pd__actions">
              <button
                class="pd__btn pd__btn--ghost"
                :disabled="!store.id"
                @click="goToStore"
              >
                <i class="pi pi-arrow-right" />
                {{ t('paymentsMgmt.viewStore') }}
              </button>
            </div>
          </div>

          <!-- Commande -->
          <div v-if="order" class="pd__card">
            <div class="pd__card-hd">
              <h3 class="pd__card-title">
                <i class="pi pi-shopping-bag" />
                {{ t('paymentsMgmt.detailOrder') }}
              </h3>
            </div>
            <div class="pd__info-view">
              <div class="pd__info-row">
                <span class="pd__info-label">{{
                  t('paymentsMgmt.orderNumber')
                }}</span>
                <span class="pd__info-value">{{
                  order.orderNumber ?? '—'
                }}</span>
              </div>
              <div class="pd__info-row">
                <span class="pd__info-label">{{
                  t('paymentsMgmt.orderStatus')
                }}</span>
                <span class="pd__info-value">{{ order.status ?? '—' }}</span>
              </div>
              <div v-if="order.paymentStatus" class="pd__info-row">
                <span class="pd__info-label">{{
                  t('paymentsMgmt.orderPaymentStatus')
                }}</span>
                <span
                  class="pd__pill pd__pill--inline"
                  :style="{
                    background:
                      orderPaymentStatusColor(order.paymentStatus) + '1a',
                    color: orderPaymentStatusColor(order.paymentStatus),
                    borderColor:
                      orderPaymentStatusColor(order.paymentStatus) + '40',
                  }"
                >
                  {{ orderPaymentStatusLabel(order.paymentStatus) }}
                </span>
              </div>
              <div class="pd__info-row">
                <span class="pd__info-label">{{
                  t('paymentsMgmt.orderTotal')
                }}</span>
                <span class="pd__info-value">{{
                  formatAmount(order.totalPrice, payment.cur)
                }}</span>
              </div>
            </div>
            <div class="pd__actions">
              <button class="pd__btn pd__btn--ghost" @click="goToOrder">
                <i class="pi pi-arrow-right" />
                {{ t('paymentsMgmt.viewOrder') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </template>

    <RefundDialog
      :open="refundDialogOpen"
      :payment-id="paymentId"
      :max-amount="payment?.amount ?? null"
      :currency="payment?.cur"
      @close="closeRefundDialog"
      @refunded="onRefunded"
    />
  </div>
</template>

<style scoped>
.pd {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 2.5rem 3rem;
  box-sizing: border-box;
}
@media (max-width: 1023px) {
  .pd {
    padding: 1.5rem;
  }
}

.pd__bc {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
}
.pd__bc-link {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: 0.8125rem;
  font-family: inherit;
  color: inherit;
}
.pd__bc-link:hover {
  color: #0b1320;
}
.pd__bc-sep {
  font-size: 0.625rem;
}
.pd__bc-cur {
  font-weight: 700;
  color: var(--nxt-on-surface);
  font-family: monospace;
}

.pd__loading,
.pd__error {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 4rem 1rem;
  color: var(--nxt-on-surface-muted);
}
.pd__error i {
  font-size: 2rem;
  color: var(--nxt-error);
}

.pd__header {
  display: flex;
  align-items: center;
  gap: 1rem;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: 1.5rem;
  padding: 1.5rem 1.75rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.04);
}
.pd__back {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  flex-shrink: 0;
  border: 1px solid var(--nxt-outline-variant);
  background: var(--nxt-surface-low);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9375rem;
  color: var(--nxt-on-surface-variant);
  cursor: pointer;
}
.pd__header-info {
  min-width: 0;
  flex: 1;
}
.pd__amount {
  margin: 0 0 0.15rem;
  font-size: 1.75rem;
  font-weight: 800;
  color: var(--nxt-on-surface);
}
.pd__sub {
  margin: 0;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
}
.pd__pill {
  flex-shrink: 0;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 0.3rem 0.875rem;
  border-radius: 0.5rem;
  border: 1px solid;
}
.pd__pill--inline {
  font-size: 0.6875rem;
  padding: 0.15rem 0.625rem;
}

.pd__header-actions {
  display: flex;
  gap: 0.625rem;
  flex-shrink: 0;
}
.pd__reconcile-error {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0;
  padding: 0.75rem 1rem;
  border-radius: 0.75rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--nxt-error);
  background: rgba(220, 38, 38, 0.08);
}
.pd__btn--danger {
  background: var(--nxt-error);
  color: #fff;
  box-shadow: 0 2px 8px rgba(220, 38, 38, 0.35);
}
.pd__spinner {
  width: 0.875rem;
  height: 0.875rem;
  border-radius: 50%;
  display: inline-block;
  border: 2px solid rgba(11, 19, 32, 0.2);
  border-top-color: currentColor;
  animation: pd-spin 0.7s linear infinite;
}
@keyframes pd-spin {
  to {
    transform: rotate(360deg);
  }
}

/* Grille principale 2 colonnes */
.pd__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  align-items: start;
}
@media (max-width: 900px) {
  .pd__grid {
    grid-template-columns: 1fr;
  }
}
.pd__col {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.pd__card {
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: 1.5rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.04);
  overflow: hidden;
}
.pd__card-hd {
  padding: 1.125rem 1.5rem;
  border-bottom: 1px solid var(--nxt-outline-variant);
}
.pd__card-title {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}

.pd__info-view {
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
  padding: 1.5rem;
}
.pd__info-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  font-size: 0.875rem;
}
.pd__info-label {
  color: var(--nxt-on-surface-muted);
  font-weight: 600;
}
.pd__info-value {
  color: var(--nxt-on-surface);
  font-weight: 700;
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 60%;
}

.pd__entity-row {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  padding: 1.5rem;
}
.pd__entity-avatar {
  width: 3rem;
  height: 3rem;
  min-width: 3rem;
  border-radius: 50%;
  background: #0b1320;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1rem;
}
.pd__entity-info {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  min-width: 0;
}
.pd__entity-name {
  font-size: 1rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}
.pd__entity-id {
  font-size: 0.75rem;
  color: var(--nxt-on-surface-muted);
  font-family: monospace;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pd__tx-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 1.25rem 1.5rem;
}
.pd__tx-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 0.875rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.75rem;
  font-size: 0.8125rem;
}
.pd__tx-type {
  font-weight: 700;
  color: var(--nxt-on-surface);
}
.pd__tx-status {
  color: var(--nxt-on-surface-muted);
}
.pd__tx-amount {
  margin-left: auto;
  font-weight: 700;
  color: var(--nxt-on-surface);
}
.pd__tx-date {
  color: var(--nxt-on-surface-muted);
  font-size: 0.75rem;
}

.pd__actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1.125rem 1.5rem;
  border-top: 1px solid var(--nxt-outline-variant);
}
.pd__actions .pd__btn {
  flex: 1;
  justify-content: center;
}
.pd__btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  height: 2.5rem;
  padding: 0 1.25rem;
  border-radius: 0.625rem;
  font-size: 0.875rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  border: none;
}
.pd__btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.pd__btn--ghost {
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface-variant);
  border: 1px solid var(--nxt-outline-variant);
}
</style>

<style>
[data-theme='dark'] .pd__header,
[data-theme='dark'] .pd__card {
  background: rgba(20, 28, 41, 0.8);
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .pd__back,
[data-theme='dark'] .pd__btn--ghost {
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.7);
  border-color: rgba(255, 255, 255, 0.1);
}
[data-theme='dark'] .pd__tx-row {
  border-color: rgba(255, 255, 255, 0.08);
}
</style>
